import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { getStoredPartner, partnerHome, withPartner } from '../lib/partnerContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function PaymentSuccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const partnerSub = getStoredPartner();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [error, setError] = useState(null);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      verifyPaymentAndLogin(sessionId);
    } else {
      setStatus('error');
      setError('Session de paiement non trouvée');
    }
  }, [searchParams]);

  const verifyPaymentAndLogin = async (sessionId) => {
    try {
      // Defensive: if this is a Conseil payment, redirect to the proper confirmation page
      try {
        const txCheck = await axios.get(`${API}/payments/transaction-type/${sessionId}`);
        const txType = txCheck.data?.type;
        const conseilId = txCheck.data?.conseil_id;
        if (txType === 'conseil_strategique') {
          navigate(`/conseil/confirmation?session_id=${sessionId}${conseilId ? `&conseil_id=${conseilId}` : ''}`, { replace: true });
          return;
        }
      } catch (_e) {
        // if endpoint fails, continue with normal flow
      }
      
      // Call the verify-and-login endpoint
      const response = await axios.get(`${API}/payments/verify-and-login/${sessionId}`);
      
      if (response.data.access_token) {
        // Store token and user info
        localStorage.setItem('token', response.data.access_token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        
        // Update auth context
        login(response.data.access_token, response.data.user);
        
        setStatus('success');
        toast.success('Paiement confirmé ! Bienvenue.');
        
        // Redirect to dashboard after 2 seconds
        setTimeout(() => {
          navigate('/dashboard', { replace: true });
        }, 2000);
      } else {
        throw new Error('Token non reçu');
      }
    } catch (error) {
      console.error('Verification error:', error);
      setStatus('error');
      setError(error.response?.data?.detail || 'Erreur lors de la vérification du paiement');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-navy to-navy/90 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-2xl">
        <CardContent className="p-8 text-center">
          {status === 'verifying' && (
            <>
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gold/20 mb-6">
                <Loader2 className="w-8 h-8 text-gold animate-spin" />
              </div>
              <h1 className="font-heading text-2xl font-bold text-navy mb-2">
                Vérification du paiement...
              </h1>
              <p className="text-muted-foreground">
                Connexion à votre compte en cours...
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-100 mb-6">
                <CheckCircle className="w-10 h-10 text-emerald-600" />
              </div>
              <h1 className="font-heading text-2xl font-bold text-navy mb-2">
                Paiement confirmé !
              </h1>
              <p className="text-muted-foreground mb-4">
                Bienvenue ! Redirection vers votre espace...
              </p>
              <div className="animate-pulse">
                <Loader2 className="w-5 h-5 text-gold mx-auto animate-spin" />
              </div>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 mb-6">
                <AlertCircle className="w-10 h-10 text-red-600" />
              </div>
              <h1 className="font-heading text-2xl font-bold text-navy mb-2">
                Oups !
              </h1>
              <p className="text-muted-foreground mb-6">
                {error || 'Une erreur est survenue'}
              </p>
              <div className="space-y-2">
                <Button
                  onClick={() => navigate(withPartner('/auth', partnerSub))}
                  className="w-full bg-gold hover:bg-gold/90 text-black"
                >
                  Se connecter manuellement
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate(partnerHome(partnerSub))}
                  className="w-full"
                >
                  Retour à l'accueil
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-4">
                Votre paiement a été validé. Si vous avez des difficultés à accéder à votre compte, 
                connectez-vous avec l'email et le mot de passe que vous avez choisis.
              </p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
